import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router";
import { Calendar, ChevronDown, Check } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@web/components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@web/components/ui/popover";
import { Button } from "@web/components/ui/button";
import {
  DateRangePickerModal,
  getCurrentMonthRange,
  formatDateRangeDisplay,
  type DateRange,
} from "@web/components/ui/date-range-picker-modal";
import { useCategoryStore, useMethodStore, useWorkspaceStore } from "@web/stores";
import type { LogListQuery, LogType } from "@repo/share-log-interfaces";
import { cn } from "@repo/share-log-web-utils";

interface LogFilterProps {
  workspaceId: number;
  onFilterChange: (filter: LogListQuery) => void;
}

const ALL_VALUE = "all";
const MAX_VISIBLE_SELECTED = 2;

function parseCsvIds(value: string | null): number[] {
  if (!value) return [];
  return value
    .split(",")
    .filter(Boolean)
    .map(Number)
    .filter((n) => !isNaN(n) && n > 0);
}

function getFilterFromSearchParams(searchParams: URLSearchParams): {
  dateRange: DateRange;
  type: string;
  userId: string;
  categoryIds: number[];
  methodIds: number[];
} {
  const defaultRange = getCurrentMonthRange();
  return {
    dateRange: {
      startDate: searchParams.get("startDate") ?? defaultRange.startDate,
      endDate: searchParams.get("endDate") ?? defaultRange.endDate,
    },
    type: searchParams.get("type") ?? ALL_VALUE,
    userId: searchParams.get("userId") ?? ALL_VALUE,
    categoryIds: parseCsvIds(searchParams.get("categoryId")),
    methodIds: parseCsvIds(searchParams.get("methodId")),
  };
}

function buildLogListQuery(params: {
  dateRange: DateRange;
  type: string;
  userId: string;
  categoryIds: number[];
  methodIds: number[];
}): LogListQuery {
  const filter: LogListQuery = {};
  if (params.dateRange.startDate) filter.startDate = params.dateRange.startDate;
  if (params.dateRange.endDate) filter.endDate = params.dateRange.endDate;
  if (params.type !== ALL_VALUE) filter.type = params.type as LogType;
  if (params.userId !== ALL_VALUE) filter.userId = Number(params.userId);
  if (params.categoryIds.length > 0) filter.categoryId = params.categoryIds;
  if (params.methodIds.length > 0) filter.methodId = params.methodIds;
  return filter;
}

interface MultiSelectDropdownProps {
  label: string;
  items: { id: number; name: string }[];
  selectedIds: number[];
  onToggle: (id: number) => void;
  className?: string;
}

function MultiSelectDropdown({
  label,
  items,
  selectedIds,
  onToggle,
  className,
}: MultiSelectDropdownProps) {
  const selectedItems = items.filter((item) => selectedIds.includes(item.id));

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={cn(
            "flex items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm",
            "hover:bg-accent hover:text-accent-foreground transition-colors",
            "focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring",
            className,
          )}
        >
          {selectedIds.length === 0 ? (
            <span>{label}: 전체</span>
          ) : (
            <span className="flex items-center gap-1.5 overflow-hidden">
              <span className="shrink-0 text-muted-foreground">{label}</span>
              <span className="flex items-center gap-1 overflow-hidden">
                {selectedItems.slice(0, MAX_VISIBLE_SELECTED).map((item) => (
                  <span
                    key={item.id}
                    className="inline-flex items-center gap-0.5 rounded bg-primary/10 px-1.5 py-0.5 text-xs font-medium text-primary truncate max-w-20"
                  >
                    {item.name}
                  </span>
                ))}
                {selectedItems.length > MAX_VISIBLE_SELECTED && (
                  <span className="text-xs text-muted-foreground shrink-0">
                    +{selectedItems.length - MAX_VISIBLE_SELECTED}
                  </span>
                )}
              </span>
            </span>
          )}
          <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-56 p-2" align="start">
        <div className="max-h-60 overflow-y-auto">
          {items.map((item) => {
            const isSelected = selectedIds.includes(item.id);
            return (
              <button
                key={item.id}
                type="button"
                role="checkbox"
                aria-checked={isSelected}
                onClick={() => onToggle(item.id)}
                className={cn(
                  "flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-sm",
                  "hover:bg-accent hover:text-accent-foreground transition-colors",
                )}
              >
                <div
                  className={cn(
                    "flex h-4 w-4 shrink-0 items-center justify-center rounded-sm border shadow-sm",
                    isSelected
                      ? "border-primary bg-primary text-primary-foreground"
                      : "border-primary",
                  )}
                >
                  {isSelected && <Check className="h-3 w-3" />}
                </div>
                <span className="truncate">{item.name}</span>
              </button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}

export function LogFilter({ workspaceId, onFilterChange }: LogFilterProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const { categories, fetchCategories } = useCategoryStore();
  const { methods, fetchMethods } = useMethodStore();
  const { members, fetchMembers } = useWorkspaceStore();

  const initialParams = getFilterFromSearchParams(searchParams);
  const [dateRange, setDateRange] = useState<DateRange>(
    initialParams.dateRange,
  );
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const [type, setType] = useState<string>(initialParams.type);
  const [userId, setUserId] = useState<string>(initialParams.userId);
  const [categoryIds, setCategoryIds] = useState<number[]>(
    initialParams.categoryIds,
  );
  const [methodIds, setMethodIds] = useState<number[]>(
    initialParams.methodIds,
  );

  const toggleId = useCallback(
    (setter: React.Dispatch<React.SetStateAction<number[]>>, id: number) => {
      setter((prev) =>
        prev.includes(id) ? prev.filter((v) => v !== id) : [...prev, id],
      );
    },
    [],
  );

  const updateSearchParams = useCallback(
    (params: {
      dateRange: DateRange;
      type: string;
      userId: string;
      categoryIds: number[];
      methodIds: number[];
    }) => {
      const defaultRange = getCurrentMonthRange();
      const newParams = new URLSearchParams();

      if (
        params.dateRange.startDate &&
        params.dateRange.startDate !== defaultRange.startDate
      ) {
        newParams.set("startDate", params.dateRange.startDate);
      }
      if (
        params.dateRange.endDate &&
        params.dateRange.endDate !== defaultRange.endDate
      ) {
        newParams.set("endDate", params.dateRange.endDate);
      }
      if (params.type !== ALL_VALUE) {
        newParams.set("type", params.type);
      }
      if (params.userId !== ALL_VALUE) {
        newParams.set("userId", params.userId);
      }
      if (params.categoryIds.length > 0) {
        newParams.set("categoryId", params.categoryIds.join(","));
      }
      if (params.methodIds.length > 0) {
        newParams.set("methodId", params.methodIds.join(","));
      }

      setSearchParams(newParams);
    },
    [setSearchParams],
  );

  useEffect(() => {
    void fetchCategories(workspaceId);
    void fetchMethods(workspaceId);
    void fetchMembers(workspaceId);
  }, [workspaceId, fetchCategories, fetchMethods, fetchMembers]);

  // URL 파라미터 변경 시 (초기 로드, 뒤로가기/앞으로가기) 필터 상태 동기화 및 데이터 fetch
  useEffect(() => {
    if (!workspaceId) return;

    const params = getFilterFromSearchParams(searchParams);

    setDateRange(params.dateRange);
    setType(params.type);
    setUserId(params.userId);
    setCategoryIds(params.categoryIds);
    setMethodIds(params.methodIds);

    onFilterChange(buildLogListQuery(params));
  }, [workspaceId, searchParams, onFilterChange]);

  const handleApplyFilter = () => {
    const params = { dateRange, type, userId, categoryIds, methodIds };
    updateSearchParams(params);
  };

  const handleReset = () => {
    setSearchParams(new URLSearchParams());
  };

  const handleDateRangeChange = (range: DateRange) => {
    setDateRange(range);
    const params = { dateRange: range, type, userId, categoryIds, methodIds };
    updateSearchParams(params);
  };

  return (
    <div className="flex flex-wrap gap-3 items-center">
      {/* Date Range Picker Button */}
      <Button
        variant="outline"
        onClick={() => setDatePickerOpen(true)}
        className="min-w-48 justify-start"
      >
        <Calendar className="h-4 w-4 mr-2" />
        {formatDateRangeDisplay(dateRange)}
      </Button>

      <DateRangePickerModal
        open={datePickerOpen}
        onOpenChange={setDatePickerOpen}
        value={dateRange}
        onChange={handleDateRangeChange}
      />

      <Select value={type} onValueChange={setType}>
        <SelectTrigger className="w-32">
          <SelectValue placeholder="유형" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={ALL_VALUE}>유형: 전체</SelectItem>
          <SelectItem value="expense">지출</SelectItem>
          <SelectItem value="income">수입</SelectItem>
        </SelectContent>
      </Select>

      <Select value={userId} onValueChange={setUserId}>
        <SelectTrigger className="w-36">
          <SelectValue placeholder="사용자" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={ALL_VALUE}>사용자: 전체</SelectItem>
          {members.map((member) => (
            <SelectItem key={member.userId} value={String(member.userId)}>
              {member.user.nickname || member.user.email}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <MultiSelectDropdown
        label="카테고리"
        items={categories}
        selectedIds={categoryIds}
        onToggle={(id) => toggleId(setCategoryIds, id)}
        className="min-w-40"
      />

      <MultiSelectDropdown
        label="수단"
        items={methods}
        selectedIds={methodIds}
        onToggle={(id) => toggleId(setMethodIds, id)}
        className="min-w-36"
      />

      <div className="flex gap-2">
        <Button onClick={handleApplyFilter} size="sm">
          적용
        </Button>
        <Button onClick={handleReset} variant="outline" size="sm">
          초기화
        </Button>
      </div>
    </div>
  );
}
